import { defineStore } from 'pinia';
import HiCache from 'utils/cache';
import sysConfig from 'service/sysConfig';
import { CompanyListType, Game, MenuListType } from 'types/game';
import { useAppStore } from 'store/modules/appStore';
import { useT } from 'core/i18n';

interface ConfigState {
    favoriteGameIds: string[];
    favoriteGameList: Game[];
    recentGameList: Game[];
    recentGameIds: string[];
    companyList: CompanyListType[];
    typeValue: number;
    providerIds: number[];
    menuList: MenuListType[];
    backGameType: number;
}

export const useConfigStore = defineStore({
    id: 'config',
    state: (): ConfigState => ({
        // 收藏列表ID
        favoriteGameIds: [],

        // 收藏列表数据
        favoriteGameList: [],

        // 最近进入游戏的ID
        recentGameIds: [],

        // 最近进入的游戏
        recentGameList: [],

        // 公司列表
        companyList: [],

        typeValue: HiCache.getCache('typeValue') || 1,

        providerIds: [],
        menuList: [],

        backGameType: -9527,
    }),
    getters: {
        // 收藏列表
        FavoriteGameIds: (state: ConfigState) => state.favoriteGameIds,

        // 收藏列表数据
        FavoriteGameList: (state: ConfigState) => state.favoriteGameList,

        // 最近进入游戏的ID
        RecentGameIds: (state: ConfigState) => state.recentGameIds,

        // 最近进入的游戏
        RecentGameList: (state: ConfigState) => state.recentGameList,

        // 公司列表
        CompanyList: (state: ConfigState) => state.companyList,

        // 类型
        TypeValue: (state: ConfigState) => state.typeValue,

        getProviderIds: (state: ConfigState) => state.providerIds,
        MenuList: (state: ConfigState) => state.menuList,
        BackGameType: (state: ConfigState) => state.backGameType,
    },
    actions: {
        // 设置返回游戏类型
        setBackGameType(value: number) {
            console.log(value, 'backGameType -----------------------------------------1');
            this.backGameType = value;
        },
        // 设置类型
        setProviderIds(value: number[]) {
            this.providerIds = value;
        },
        // 添加收藏addFavoriteGameId
        addFavoriteGameId(game: Game) {
            const id = game.id.toString();
            // 判断是否在收藏列表中
            if (this.favoriteGameIds.includes(id)) {
                this.favoriteGameIds = this.favoriteGameIds.filter(item => item !== id);
                // 根据最终的id去去除favoriteGameList中的数据
                this.favoriteGameList = this.favoriteGameList.filter(item => this.favoriteGameIds.includes(item.id.toString()));
            } else {
                // 判断是否超过了最大收藏数30
                if (this.favoriteGameIds.length >= 30) {
                    // 移除第一个
                    this.favoriteGameIds.shift();
                    this.favoriteGameList.shift();
                }
                this.favoriteGameIds.push(id);
                this.favoriteGameList.push(game);
            }
            HiCache.setCache('favoriteGameIds', this.favoriteGameIds.join(','));
        },

        // 添加最近的游戏
        addRecentGameId(game: Game) {
            const id = game.id.toString();
            // 判断是否在最近列表中
            if (this.recentGameIds.includes(id)) {
                this.recentGameIds = this.recentGameIds.filter(item => item !== id);
                // 根据最终的id去去除recentGameList中的数据
                this.recentGameList = this.recentGameList.filter(item => this.recentGameIds.includes(item.id.toString()));
            }
            // 判断是否超过了最大收藏数30
            if (this.recentGameIds.length >= 30) {
                // 移除第一个
                this.recentGameIds.shift();
                this.recentGameList.shift();
            }
            this.recentGameIds.push(id);
            this.recentGameList.push(game);
            HiCache.setCache('recentGameIds', this.recentGameIds.join(','));
        },

        // 替换收藏id
        setFavoriteGameIds(ids: string[]) {
            this.favoriteGameIds = ids;
            HiCache.setCache('favoriteGameIds', this.favoriteGameIds.join(','));
        },

        // 替换最近游戏id
        setRecentGameIds(ids: string[]) {
            this.recentGameIds = ids;
            HiCache.setCache('recentGameIds', this.recentGameIds.join(','));
        },

        // 获取收藏列表
        async getFavoriteGameIds({ ids, isRecent = false }) {
            try {
                const response = await sysConfig.findGameInfoList({ gameIds: ids });
                const { code, data } = response;
                if (code === 0) {
                    console.log(data, '------data');
                    const records = data.records;
                    if (!records) {
                        return;
                    }
                    if (!isRecent) {
                        this.favoriteGameList = records;
                    } else {
                        this.recentGameList = records;
                    }
                }
            } catch (e) {
                console.log(e, '---------e');
            }
        },

        // 获取公司列表
        async getCompanyList(params) {
            try {
                const response = await sysConfig.searchCompany(params);
                const { code, data } = response;
                if (code === 0) {
                    this.companyList = data;
                    return data;
                }
            } catch (e) {
                console.log(e, '---------e');
            }
        },

        // 获取公司列表
        async getCompanyListV3(params) {
            try {
                const response = await sysConfig.searchCompanyV3(params);
                const { code, data } = response;
                if (code === 0) {
                    this.companyList = data;
                    return data;
                }
            } catch (e) {
                console.log(e, '---------e');
            }
        },

        // 网站公告全部已读
        async getMessageReadAll() {
            try {
                return await sysConfig.messageReadAll();
            } catch (e) {
                return Promise.reject(e);
            }
        },
        // 消息删除全部已读
        async getDeleteAllRead() {
            try {
                return await sysConfig.deleteAllRead();
            } catch (e) {
                return Promise.reject(e);
            }
        },
        // 消息删除 id
        async getMessageDelete(data) {
            try {
                return await sysConfig.deleteReadId(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },
        // 已读消息 id
        async getMessageRead(data) {
            try {
                return await sysConfig.readMessageId(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },
        // 获取消息列表
        async getMessageList(data) {
            try {
                return await sysConfig.findMessageList(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },
        // 获取菜单列表
        async getMenuList() {
            try {
                const res = await sysConfig.findMenuList();
                if (res?.code === 0) {
                    this.menuList = res?.data || [];
                }
            } catch (e) {
                return Promise.reject(e);
            } finally {
            }
        },
    },
});
